import React, { useState } from 'react';
import styles from './ConfigurationForm.module.css';
import {
  AnalysisConfig,
  AnalysisMethod,
  Severity
} from '@/api/domains/analyses/types';
import {
  InfoDialog,
  InfoDialogType,
  PillSelector,
  PresetCard,
  RadarVisualizer,
  SegmentedControl,
  SliderControl
} from './components';

interface ConfigurationFormProps {
  config: AnalysisConfig;
  onChange: (config: AnalysisConfig) => void;
}

export const ConfigurationForm: React.FC<ConfigurationFormProps> = ({
  config,
  onChange
}) => {
  const [infoDialog, setInfoDialog] = useState<{
    visible: boolean;
    type: InfoDialogType;
  }>({
    visible: false,
    type: null
  });

  const applyPreset = (method: AnalysisMethod) => {
    const presets: Record<AnalysisMethod, Partial<AnalysisConfig>> = {
      VECTOR_ONLY: {
        vector_threshold: 0.3,
        top_k_candidates: 3,
        min_confidence_score: 0.4,
        min_severity: 'LOW' as Severity
      },
      HYBRID: {
        vector_threshold: 0.5,
        top_k_candidates: 5,
        min_confidence_score: 0.6,
        min_severity: 'MEDIUM' as Severity
      },
      RAG_ENHANCED: {
        vector_threshold: 0.8,
        top_k_candidates: 10,
        min_confidence_score: 0.8,
        min_severity: 'HIGH' as Severity
      }
    };
    onChange({ ...config, method, ...presets[method] });
  };

  const showInfo = (type: InfoDialogType) =>
    setInfoDialog({ visible: true, type });

  return (
    <div className={styles.bentoContainer}>
      <InfoDialog
        visible={infoDialog.visible}
        type={infoDialog.type}
        onHide={() => setInfoDialog({ visible: false, type: null })}
      />

      <div className={styles.presetsGrid}>
        {(['VECTOR_ONLY', 'HYBRID', 'RAG_ENHANCED'] as AnalysisMethod[]).map(
          (m) => (
            <PresetCard
              key={m}
              method={m}
              active={config.method === m}
              onApply={applyPreset}
              onInfo={showInfo as any}
            />
          )
        )}
      </div>

      <div className={styles.mainGrid}>
        <div className={styles.visualizerBox}>
          <div className={styles.chartTitle}>
            <i className="pi pi-chart-line" />
            <h3>Analysis Balance</h3>
          </div>
          <RadarVisualizer config={config} />
        </div>

        <div className={styles.controlsColumn}>
          <div className={styles.controlBox}>
            <div className={styles.boxHeaderRow}>
              <label>Match Strictness</label>
              <i
                className="pi pi-question-circle"
                onClick={() => showInfo('STRICTNESS')}
              />
            </div>
            <SegmentedControl
              options={[
                { value: 0.2, label: 'Loose' },
                { value: 0.5, label: 'Balanced' },
                { value: 0.8, label: 'Strict' }
              ]}
              value={config.vector_threshold}
              onChange={(v) => onChange({ ...config, vector_threshold: v })}
            />
          </div>

          <div className={styles.controlBox}>
            <div className={styles.boxHeaderRow}>
              <label>Search Depth</label>
              <i
                className="pi pi-question-circle"
                onClick={() => showInfo('DEPTH')}
              />
              <span className={styles.badge}>{config.top_k_candidates}</span>
            </div>
            <SliderControl
              value={config.top_k_candidates}
              min={1}
              max={10}
              step={1}
              onChange={(v) => onChange({ ...config, top_k_candidates: v })}
              formulaText={
                <>
                  System will process ~{10 * config.top_k_candidates} potential
                  overlaps per regulation.
                </>
              }
            />
          </div>

          <div className={styles.controlBox}>
            <div className={styles.boxHeaderRow}>
              <label>AI Confidence Threshold</label>
              <i
                className="pi pi-question-circle"
                onClick={() => showInfo('CONFIDENCE')}
              />
              <span className={styles.badge}>
                {Math.round(config.min_confidence_score * 100)}%
              </span>
            </div>
            <SliderControl
              value={config.min_confidence_score}
              min={0}
              max={1}
              step={0.05}
              onChange={(v) => onChange({ ...config, min_confidence_score: v })}
              formulaText={
                <>
                  Only findings with AI confidence ≥{' '}
                  {Math.round(config.min_confidence_score * 100)}% will be
                  shown.
                </>
              }
            />
          </div>

          <div className={styles.controlBox}>
            <div className={styles.boxHeaderRow}>
              <label>Min. Issue Severity</label>
            </div>
            <PillSelector
              options={['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']}
              value={config.min_severity}
              onChange={(v) => onChange({ ...config, min_severity: v })}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
